// What a standby is waiting behind: the reservations that hold the aircraft
// during its window, and the standbys already queued ahead of it for the same
// time (the queue is first come, first served by creation time; see
// apps/api/src/domain/optionQueue.ts). Pure: the form passes the bookings it
// has already loaded.

export interface OutlookCandidate {
  bookingId: number
  registration: string
  kind: string
  status: string
  startsAt: string
  endsAt: string
  createdAt: string
  // A standby past its drop time is as good as gone (promotion skips it).
  expiresAt?: string | null
}

export interface StandbyOutlook<T extends OutlookCandidate> {
  // Reservations in the way: the standby is promoted only when ALL are gone.
  holding: T[]
  // Standbys that would be promoted before it.
  ahead: T[]
}

export function standbyOutlook<T extends OutlookCandidate>(
  bookings: readonly T[],
  registration: string,
  window: { startsAt: Date; endsAt: Date },
  options: {
    excludeBookingId?: number
    // Only standbys ahead of this one: made before it, or at the same instant
    // with a lower id (the order promotion uses).
    before?: { createdAt: string; bookingId: number }
    now?: Date
  } = {},
): StandbyOutlook<T> {
  const now = (options.now ?? new Date()).getTime()
  const start = window.startsAt.getTime()
  const end = window.endsAt.getTime()
  const holding: T[] = []
  const ahead: T[] = []

  for (const booking of bookings) {
    if (booking.registration !== registration || booking.status !== 'active') continue
    if (booking.bookingId === options.excludeBookingId) continue
    // Half-open, like the database: touching is not overlapping.
    if (!(new Date(booking.startsAt).getTime() < end && new Date(booking.endsAt).getTime() > start)) continue

    if (booking.kind === 'reservation') holding.push(booking)
    else if (booking.kind === 'option') {
      if (booking.expiresAt && new Date(booking.expiresAt).getTime() <= now) continue
      const { before } = options
      if (
        before === undefined ||
        booking.createdAt < before.createdAt ||
        (booking.createdAt === before.createdAt && booking.bookingId < before.bookingId)
      ) {
        ahead.push(booking)
      }
    }
  }
  const byStart = (a: T, b: T) => a.startsAt.localeCompare(b.startsAt)
  return {
    holding: holding.sort(byStart),
    ahead: ahead.sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.bookingId - b.bookingId),
  }
}

// The "drop it if not confirmed" choices that are still ahead: a drop time
// already past would be refused (standby_drop_in_past), so it is not offered.
export function allowedDropHours(choices: readonly number[], startsAt: Date, now: Date): number[] {
  return choices.filter((hours) => startsAt.getTime() - hours * 3_600_000 > now.getTime())
}
